import type { ExperienceMode, MediaActionApprovalRequest } from '@localagent/sdk';
import { namedTarget, NO_NAMED_TARGET } from './named-target';

interface MediaActionApprovalCardProps {
  request: MediaActionApprovalRequest;
  onApprove: () => void | Promise<void>;
  onDeny: () => void | Promise<void>;
  isSubmitting?: boolean;
  error?: string | null;
  mode?: ExperienceMode;
  /** The subagent participant that asked; absent for the chat's own agent. */
  askedBy?: { participantId: string; label: string };
}

function mediaNormalCopy(toolName: string, actor: string): string {
  if (toolName === 'media_empty_trash') {
    return `${actor} wants to empty Trash`;
  }
  return `${actor} wants to move this artifact to Trash`;
}

export function MediaActionApprovalCard({
  request,
  onApprove,
  onDeny,
  isSubmitting,
  error,
  mode,
  askedBy,
}: MediaActionApprovalCardProps) {
  const actor = askedBy?.label ?? 'The agent';
  const testid = 'media-action-approval';
  const isNormal = mode !== 'advanced';
  const target = namedTarget(request.arguments);
  const args = JSON.stringify(request.arguments, null, 2);

  return (
    <div
      data-testid={`${testid}-card`}
      data-approval-kind="media_action"
      data-participant-id={askedBy?.participantId}
      className="rounded bg-warning-subtle p-2.5"
    >
      {isNormal ? (
        <p className="mb-1 text-caption font-medium text-foreground">
          {mediaNormalCopy(request.toolName, actor)}
          {target ? (
            <>
              {': '}
              <span data-testid={`${testid}-target`}>{target}</span>
            </>
          ) : (
            <>
              . <span data-testid={`${testid}-no-target`}>{NO_NAMED_TARGET}</span>
            </>
          )}
        </p>
      ) : (
        <>
          <p className="mb-1 text-caption font-medium text-foreground">
            {`${actor} wants to run`}{' '}
            <code
              data-testid={`${testid}-tool`}
              className="rounded bg-muted px-1 py-0.5 text-micro"
            >
              {request.toolName}
            </code>
          </p>
          <pre
            data-testid={`${testid}-arguments`}
            className="mb-2 max-h-40 overflow-auto rounded bg-card px-2 py-1.5 text-micro text-foreground"
          >
            {args}
          </pre>
        </>
      )}
      {error && (
        <p data-testid={`${testid}-error`} className="mb-2 text-micro text-destructive">
          {error}
        </p>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid={`${testid}-approve`}
          disabled={isSubmitting}
          className="rounded bg-warning-solid px-3 py-1 text-micro font-medium text-warning-solid-foreground hover:bg-warning-solid/90 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => {
            void onApprove();
          }}
        >
          {isSubmitting ? 'Submitting…' : 'Approve'}
        </button>
        <button
          type="button"
          data-testid={`${testid}-deny`}
          disabled={isSubmitting}
          className="rounded px-3 py-1 text-micro text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => {
            void onDeny();
          }}
        >
          Deny
        </button>
      </div>
    </div>
  );
}
